import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Flex, LegacyText, font, themedColor } from '@lemonade-hq/cdk';
import { VideoReview } from '../types';
import { VERTICAL_MEDIA_QUERIES } from './verticalMediaQueries';
import { Controls } from './Controls';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';

const ContentWrapper = styled(Flex)<{ isActive: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  pointer-events: ${({ isActive }) => (isActive ? 'auto' : 'none')};
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Video = styled.video`
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;

  &::cue {
    color: ${themedColor('white')};
    ${font('main', {
      fontSize: '1.8rem',
      fontWeight: '600',
      lineHeight: '1.5',
    })};

    ${VERTICAL_MEDIA_QUERIES.med`
            ${font('main', {
              fontSize: '1.4rem',
              fontWeight: '600',
              lineHeight: '1.5',
            })};
        `}
  }
`;

const Product = styled(LegacyText)`
  width: fit-content;
  background-color: ${themedColor('white')};
  border-radius: 100px;
  color: ${themedColor('mouseGray')};
  z-index: 1;
  min-width: 50px;
  text-align: center;

  font-size: 1.8rem;
  font-weight: 600;
  line-height: 1;
  margin: 2.8rem;
  padding: 10px 14px;

  ${MEDIA_QUERIES.mobile`
      margin: 1.2rem;
    `}

  ${VERTICAL_MEDIA_QUERIES.med`
        font-size: 1.6rem;
        padding: 0.8rem 1.2rem;
        margin: 1.4rem;
    `}
`;

const StyledGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background: linear-gradient(0deg, rgba(17, 17, 17, 0.4) 0%, rgba(17, 17, 17, 0) 100%);
  z-index: 0;
`;

const Disclosure = styled.span`
  ${font('main', { fontSize: '1rem', fontWeight: 400, lineHeight: 1.2 })};
  letter-spacing: 0.1px;
  color: ${themedColor('white')};
  text-align: center;
  z-index: 1;
  margin-bottom: 1.8rem;
  opacity: 0.7;
  white-space: pre-line;

  @media (max-width: 375px) {
    margin-bottom: 0.9rem;
  }
`;

const DISCLOSURE_DURATION = 8;

interface VideoPlayerContentProps {
  review: VideoReview;
  isActive: boolean;
  onEnd: () => void;
  isMuted: boolean;
  setIsMuted: (isMuted: boolean) => void;
  captionsOn: boolean;
  setCaptionsOn?: (isMuted: boolean) => void;
  showGradient?: boolean;
  videosDisclosure?: string;
}

export const VideoPlayerContent: React.FC<VideoPlayerContentProps> = ({
  review: { videoUrl, id, product, reviewerName, captionsUrl, posterUrl },
  isActive,
  onEnd,
  isMuted,
  setIsMuted,
  captionsOn,
  setCaptionsOn,
  showGradient = false,
  videosDisclosure,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isPlaying = useRef(false);

  const [isPaused, setIsPaused] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const loadVideo = useCallback(() => {
    const sourceElement = videoRef.current?.querySelector('source');
    if (sourceElement && !isVideoReady) {
      sourceElement.setAttribute('src', videoUrl);
      videoRef.current?.load();
    }
  }, [isVideoReady, videoUrl]);

  const handleLoadedData = useCallback(() => {
    setIsVideoReady(true);
    if (isPlaying.current && videoRef.current?.paused) {
      videoRef.current.play();
    }
  }, []);

  const play = useCallback(() => {
    isPlaying.current = true;

    loadVideo();
    if (videoRef.current && isVideoReady) {
      videoRef.current.play();
      setIsPaused(false);
    }
  }, [loadVideo, isVideoReady]);

  const pause = useCallback((): void => {
    isPlaying.current = false;

    videoRef.current?.pause();
    setIsPaused(true);
  }, []);

  const togglePlay = useCallback(() => {
    if (isPaused) {
      play();
    } else {
      pause();
    }
  }, [isPaused, play, pause]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.textTracks[0].mode = captionsOn ? 'showing' : 'hidden';

      const cues = videoRef.current.textTracks[0].cues;
      if (!cues) return;

      for (let i = 0; i < cues.length; i++) {
        const cue = cues[i] as VTTCue;
        cue.line = -6;
        cue.align = 'center';
        cue.size = 90;
      }
    }
  }, [captionsOn]);

  const stop = useCallback((): void => {
    pause();
    setProgress(0);
    if (videoRef.current) videoRef.current.currentTime = 0;
  }, [pause]);

  const seekProgress = useCallback((progress: number): void => {
    if (!videoRef.current || videoRef.current?.networkState != 1) return;
    videoRef.current.currentTime = progress * videoRef.current.duration;
  }, []);

  const onProgress = useCallback((): void => {
    const { current } = videoRef;

    if (!current || !current.duration || isNaN(current.duration)) return;
    const progress = (current.currentTime / current.duration) * 100;
    setProgress(progress);
  }, []);

  useEffect(() => {
    if (isActive) {
      play();
    } else {
      stop();
    }
  }, [isActive, play, stop]);

  return (
    <ContentWrapper isActive={isActive}>
      <Video
        className="main-player"
        ref={videoRef}
        key={id}
        playsInline
        onClick={togglePlay}
        poster={posterUrl?.src ?? ''}
        onLoadedData={handleLoadedData}
        preload="auto"
        onTimeUpdate={onProgress}
        onEnded={onEnd}
      >
        <source type="video/webm" />
        <track src={captionsUrl} label="English" kind="captions" srcLang="en-us" />
      </Video>
      {showGradient && <StyledGradient />}
      <Controls
        progress={progress}
        togglePlay={togglePlay}
        isPaused={isPaused}
        isMuted={isMuted}
        setIsMuted={setIsMuted}
        reviewerName={reviewerName}
        seekProgress={seekProgress}
        captionsOn={captionsOn}
        setCaptionsOn={setCaptionsOn}
      />
      {product != null && <Product type="main">{product[0]}</Product>}

      {videosDisclosure != null &&
        videoRef.current &&
        videoRef.current.duration &&
        videoRef.current.currentTime > videoRef.current.duration - DISCLOSURE_DURATION && (
          <Disclosure>{videosDisclosure}</Disclosure>
        )}
    </ContentWrapper>
  );
};
